import { useMemo } from "react";

import { type SystemStyleObject, useStyleConfig } from "@hightouch/htui";

type StyleObject = Record<string, unknown>;

/**
 * The design-system `Textarea` chrome for a container that frames an inner
 * editable element, so the container reads as a stock textarea.
 */
export function useTextareaChrome({ isDisabled = false } = {}) {
  // The full resolved style object for a stock DS Textarea (base + `outline`
  // variant), keyed with Chakra pseudo-selectors (`_hover`, `_focus`,
  // `_invalid`, `_disabled`, …).
  const dsStyle = useStyleConfig("Textarea", {
    variant: "outline",
  }) as StyleObject;

  // Whitelist of visual chrome keys copied onto the container — the resolved
  // style also carries layout keys from Chakra's default Textarea theme
  // (fixed size-md height, min-height, padding) that must not land here.
  const containerSx = useMemo(
    (): SystemStyleObject =>
      ({
        border: dsStyle.border,
        borderWidth: dsStyle.borderWidth,
        borderColor: dsStyle.borderColor,
        borderRadius: dsStyle.borderRadius,
        bg: dsStyle.bg,
        boxShadow: dsStyle.boxShadow,
        color: dsStyle.color,
        fontSize: dsStyle.fontSize,
        lineHeight: dsStyle.lineHeight,
        transitionProperty: dsStyle.transitionProperty,
        transitionDuration: dsStyle.transitionDuration,
        _hover: dsStyle._hover,
        _invalid: dsStyle._invalid,
        _disabled: dsStyle._disabled,
        // A div never fires `:focus` — focus lands on the inner element — so the
        // DS focus ring is remapped to `_focusWithin`.
        _focusWithin: isDisabled ? undefined : dsStyle._focus,
      }) as SystemStyleObject,
    [dsStyle, isDisabled],
  );

  return { dsStyle, containerSx };
}
